<script lang="ts" setup>
import type { ResponseResult } from '~kui/types/formResult';

import { Heading } from '~ds/components/ui/typography';
import { ICONS } from '~ds/lib/icons';
import { defineField, defineFormConfig } from '~kui/components/KUI/Core/Form/util/form';
import { useI18n } from 'vue-i18n';
import { z } from 'zod';

interface CountryState {
  label: string;
  value: string;
}

interface Props {
  firstName: string;
  countryStates: CountryState[];
  loading?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  loading: false,
});

const emit = defineEmits<{
  submit: [stateId: string, birthDate: Date];
  back: [];
}>();

const { t } = useI18n();
const router = useRouter();
const formResult = ref<ResponseResult | null>(null);

// Resolve through the router so the links pick up the cluster path prefix
// (Vue Router's base, set in router.options.ts) — a raw href would always
// point at the root cluster (e.g. `/terms` instead of `/au/terms`).
const termsHref = router.resolve('/terms').href;
const privacyHref = router.resolve('/privacy-policy').href;

const formConfig = defineFormConfig(
  {
    stateId: defineField({
      type: 'select',
      options: {
        label: t('general.formFields.state.label'),
        placeholder: t('general.formFields.state.placeholder'),
        options: props.countryStates,
        required: true,
        dataTestId: 'state-select',
        version: 'v2',
      },
      initialValue: '',
      schema: z.string().min(1, t('general.formFields.state.required')),
    }),
    birthDate: defineField({
      type: 'date',
      options: {
        label: t('general.formFields.birthDate.label'),
        dateFormat: 'YYYY-MM-DD',
        required: true,
        minAge: 13,
        dataTestId: 'birth-date-input',
        version: 'v2',
        hideChildLabel: true,
      },
      initialValue: '',
      schema: z.coerce.date(t('general.formFields.birthDate.required')),
    }),
    minAgeConsent: defineField({
      type: 'checkbox',
      options: {
        label: t('general.auth.minAgeConsent.labelWithLinks', {
          termsLink: `<a href="${termsHref}" target="_blank" rel="noopener noreferrer" class="underline">${t('general.auth.minAgeConsent.termsOfUse')}</a>`,
          privacyLink: `<a href="${privacyHref}" target="_blank" rel="noopener noreferrer" class="underline">${t('general.auth.minAgeConsent.privacyPolicy')}</a>`,
        }),
        htmlLabel: true,
        required: true,
      },
      initialValue: false,
      schema: z.literal(true, { message: t('general.auth.minAgeConsent.approvalRequired') }),
    }),
  },
  t('general.formFields.submit.label'),
);

const handleSubmit = (value: z.infer<typeof formConfig.schema>) => {
  formResult.value = null;
  emit('submit', value.stateId, value.birthDate);
};
</script>

<template>
  <div class="mb-3 flex items-center">
    <KUICoreButtonIcon
      :icon="ICONS.arrowLeft2"
      variant="tertiary"
      size="sm"
      :aria-label="t('general.auth.back')"
      @click="emit('back')"
    />

    <Heading
      size="xl"
      as="h1"
      class="flex-1 text-center"
    >
      {{ t('general.auth.nearlyThere') }}
    </Heading>
  </div>

  <div class="flex flex-col items-center">
    <KUICoreForm
      class="w-full"
      :form-config="formConfig"
      :form-result="formResult"
      :loading="loading"
      submit-button-size="lg"
      @submit="handleSubmit"
    />
  </div>
</template>
